'use client';

import { Typography } from '@/components/atoms/Typography/Typography';
import {
  IProjectTable,
  ProjectTable,
} from '@/components/molecules/ProjectTable/ProjectTable';
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink';
import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import { Image } from '@/components/atoms/Image/Image';
import { Video } from '@/components/atoms/Video/Video';
import { ICommonProps, TImageAsset, TVideoAsset } from '@/types/global';
import { stripUnit } from '@/utils/styling';
import { horizontaFluidPaddings } from '@/utils/sizes';
import { getContentJustifyVal } from '@/utils/styling';
import { InfoColumns } from '../InfoColumns/InfoColumns';
import { useTranslations } from 'next-intl';
import { usePageStore } from '@/store/usePageStore';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { localizedSlugs } from '@/i18n/routing';
import { capitaliseEachWord, handleClickToggleGallerySliderModal } from '@/utils/generic';
import * as Styled from './styled';


export type TColumn = {
  type: 'text' | 'table';
  content: string | IProjectTable;
};

export type TContentJustify = 'top' | 'center' | 'bottom';
export type TImagePosition = 'right' | 'fullwidth';

export interface IHeroProject extends ICommonProps {
  projectId?: string;
  image?: TImageAsset;
  imagePosition?: TImagePosition;
  video?: TVideoAsset;
  contentRows: TColumn[];
  title: string;
  imageMarginBottomOnMobile?: boolean;
}

export function HeroProject({
  image,
  contentRows,
  imagePosition = 'right',
  title,
  video,
  imageMarginBottomOnMobile = false,
  marginBottom = 'default',
}: IHeroProject) {
  const stateSelector = usePageStore;
  const projectBreadcrumbPath = stateSelector((s) => s.projectBreadcrumbPath);
  const searchParams = useSearchParams();
  const pathname = usePathname();

  /* remove param prev=presentation from URL (used to avoid navigation alert trigger) */
  useEffect(() => {
    const hasPrevParam = searchParams.get('prev') === 'presentation';

    if (hasPrevParam) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('prev');

      const newUrl =
        newParams.toString().length > 0
          ? `${pathname}?${newParams.toString()}`
          : pathname;

      if (typeof window === 'undefined') return;

      window.history.replaceState(null, '', newUrl);
    }
  }, [searchParams, pathname]);

  const ContentRow = ({ type, content }: TColumn) => {
    const components = {
      text: <Typography text={content as string} />,
      table: <ProjectTable rows={(content as IProjectTable)?.rows} />,
    };

    return components[type] || null;
  };

  const translations = useTranslations('project');
  const t = translations.raw('hero') as {
    back: string;
    backSlug: string;
  };

  const paddingExtraFromMainToContent = `${stripUnit(horizontaFluidPaddings.page.content) - stripUnit(horizontaFluidPaddings.page.main)}vw`;

  const getBreadcrumbText = () => {
    const isPresentationMode = usePageStore.getState().isPresentationMode;

    if (isPresentationMode) {
      return {
        en: 'Back to Presentation',
        es: 'Volver a la Presentación',
      };
    }

    if (!projectBreadcrumbPath) return t.back;
    
    // from homepage
    if (projectBreadcrumbPath === '/' || projectBreadcrumbPath === '/es') {
      return {
          en: 'Back to Selected Projects',
          es: 'Volver a Proyectos Seleccionados',
        }
    }
    // from work page
    else if (projectBreadcrumbPath.includes(`${localizedSlugs.work.en}/`) || projectBreadcrumbPath.includes(`${localizedSlugs.work.es}/`)) {
      const workService = projectBreadcrumbPath.split('/')[2].replaceAll(/-/g, ' ')
      return {
        en: `Back to Work / ${capitaliseEachWord(workService)}`,
        es: `Volver a Proyectos / ${capitaliseEachWord(workService)}`,
      };
    }
    
    return t.back;
  }

  return (
    <>
      <ComponentContainer
        width={'main'}
        marginBottom={marginBottom}
        marginBottomOnMobile={imagePosition === 'fullwidth' ? 'none' : 'default'}
        isProject={true}
      >
        <Styled.Container
          imagePosition={imagePosition}
          imageAspectRatio={imagePosition === 'right' ? '1' : '1/3'}
          imageMarginBottomOnMobile={imageMarginBottomOnMobile}
          data-layout={imagePosition}
        >
          <Styled.Breadcrumb>
            <CustomLink
              href={projectBreadcrumbPath || t.backSlug}
              text={getBreadcrumbText()}
              color="secondary"
              hoverColor="primary"
              scroll={false}
            ></CustomLink>
            <Typography as="h1" size="2xl" text={title} />
          </Styled.Breadcrumb>

          {imagePosition === 'right' && (
            <Styled.ContentContainer
              contentJustify={getContentJustifyVal('bottom')}
              paddingLeft={paddingExtraFromMainToContent}
              paddingRight={'0'}
            >
              {contentRows?.map((contentRow, i) => (
                <ContentRow
                  key={`row-content-${i}`}
                  type={contentRow.type}
                  content={contentRow.content}
                />
              ))}
            </Styled.ContentContainer>
          )}

          <Styled.ImageContainer
            imageAspectRatio={imagePosition === 'right' ? '1/1' : '16/9'}
            mobileHorizontalPadding={'0'}
            onClick={image ?
              (e) => handleClickToggleGallerySliderModal(e, image.src || '')
              : undefined 
            }
            className={image ? 'hero-image-container' : ''}
          >
            {video?.vimeoId ? (
              <Video
                {...(video.vimeoId ? {...video} : {})}
                poster={video.poster}
                src={video.src}
                type={video.type}
                fill={true}
                playsOnLoad={true}
                title={title}
              />
            ) : (
              image && (
                <Image 
                  {...image} 
                  includeInSlider={false} 
                  priority={true}
                  alt={image.alt}
                  className='fullwidth-mobile'
                  isNotDownloadable={true}
                />
              )
            )}
          </Styled.ImageContainer>
        </Styled.Container>
      </ComponentContainer>

      {imagePosition === 'fullwidth' && contentRows && (
        <Styled.InfoColumnsContainer>
          <InfoColumns 
            columns={contentRows} 
            width={'content'} 
            marginBottom='default'
            marginBottomOnMobile='inner'
          />
        </Styled.InfoColumnsContainer>
      )}
    </>
  );
}
